import { vestResolver } from '@hookform/resolvers/vest';
import { useCallback, useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { FormProvider, useForm } from 'react-hook-form';
import vest, { test, enforce } from 'vest';
import { motion, AnimatePresence } from 'framer-motion';
import {
  QueryLocation,
  QueryTarget,
  QueryType,
  SubmitButton,
} from '~/components';
import { useConfig, queryClient } from '~/context';
import { useDevice, useFormState, useGreeting, useStrf } from '~/hooks';
import { Directive, isQueryField, isString } from '~/types';
import { isFQDN } from '~/util';

import type { FormData, OnChangeArgs } from '~/types';

export const LookingGlassForm = (): JSX.Element => {
  const { web, messages } = useConfig();

  const greetingReady = useGreeting(s => s.greetingReady);

  const getDevice = useDevice();
  const strF = useStrf();
  const setLoading = useFormState(s => s.setLoading);
  const setStatus = useFormState(s => s.setStatus);
  const locationChange = useFormState(s => s.locationChange);
  const setTarget = useFormState(s => s.setTarget);
  const setFormValue = useFormState(s => s.setFormValue);
  const { form, filtered, selections, target } = useFormState(
    useCallback(({ form, filtered, selections, target }) => ({ form, filtered, selections, target }), []),
    isEqual,
  );

  const getDirective = useFormState(useCallback(s => s.getDirective, []));
  const resolvedOpen = useFormState(useCallback(s => s.resolvedOpen, []));
  const resetForm = useFormState(useCallback(s => s.reset, []));

  const noQueryType = strF(messages.noInput, { field: web.text.queryType });
  const noQueryLoc = strF(messages.noInput, { field: web.text.queryLocation });
  const noQueryTarget = strF(messages.noInput, { field: web.text.queryTarget });

  const queryTypes = useMemo(() => filtered.types.map(t => t.id), [filtered.types]);

  const formSchema = vest.create((data: FormData = {} as FormData) => {
    test('queryLocation', noQueryLoc, () => {
      enforce(data.queryLocation).isArrayOf(enforce.isString()).isNotEmpty();
    });
    test('queryTarget', noQueryTarget, () => {
      enforce(data.queryTarget).isArrayOf(enforce.isString()).isNotEmpty();
    });
    test('queryType', noQueryType, () => {
      enforce(data.queryType).inside(queryTypes);
    });
  });

  const formInstance = useForm<FormData>({
    resolver: vestResolver(formSchema),
    defaultValues: {
      queryTarget: [],
      queryLocation: [],
      queryType: '',
    },
  });

  const { handleSubmit, register, setValue, setError, clearErrors } = formInstance;

  const isFqdnQuery = useCallback(
    (target: string | string[], fieldType: Directive['fieldType'] | null): boolean =>
      (typeof target === 'string' || Array.isArray(target)) &&
      fieldType === 'text' &&
      isFQDN(target),
    [],
  );

  const directive = useMemo<Directive | null>(() => {
    const tmp = getDirective();
    if (tmp !== null && tmp.fieldType === null) {
      setFormValue('queryTarget', ['null']);
      setValue('queryTarget', ['null']);
    }
    return tmp;
  }, [form.queryType, form.queryLocation, getDirective]);

  function submitHandler(): void {
    if (process.env.NODE_ENV === 'development') {
      console.table({
        'Query Location': form.queryLocation.toString(),
        'Query Type': form.queryType,
        'Query Target': form.queryTarget,
        'Selected Directive': directive?.name ?? null,
      });
    }

    if (!greetingReady) {
      resetForm();
      location.reload();
      return;
    }

    // Check if the user typed an FQDN.
    // Use target.display (what the user typed) as the authoritative source —
    // form.queryTarget may have been replaced with a resolved IP from a prior FQDN selection.
    const targetToCheck =
      target.display.length > 0 ? target.display : form.queryTarget;
    const isFqdn = isFqdnQuery(targetToCheck, directive?.fieldType ?? null);

    // Always clear cached query results before re-submitting so a fresh fetch is triggered.
    for (const queryLocation of form.queryLocation) {
      const resolvedTarget = isFqdn ? targetToCheck : form.queryTarget;
      const query = { queryLocation, queryTarget: resolvedTarget, queryType: form.queryType };
      queryClient.removeQueries({ queryKey: ['/api/query', query] });
    }

    if (greetingReady && !isFqdn) {
      setStatus('results');
      return;
    }

    if (greetingReady && isFqdn) {
      // Reset queryTarget back to the display FQDN so the DNS resolver uses the right value
      setFormValue('queryTarget', [target.display]);
      setLoading(true);
      resolvedOpen();
      return;
    }
    console.group('%cSomething went wrong', 'color:red;');
    console.table({
      'Greeting Required': web.greeting.required,
      'Greeting Ready': greetingReady,
      'Query Target': form.queryTarget,
      'Query Type': form.queryType,
      'Is FQDN': isFqdn,
    });
    console.groupEnd();
  }

  const handleLocChange = (locations: string[]) =>
    locationChange(locations, { setError, clearErrors, getDevice, text: web.text });

  function handleChange(e: OnChangeArgs): void {
    if (isQueryField(e.field)) {
      setValue(e.field, e.value);
    } else {
      throw new Error(`Field '${e.field}' is not a valid form field.`);
    }

    if (e.field === 'queryLocation' && Array.isArray(e.value)) {
      handleLocChange(e.value);
    } else if (e.field === 'queryType' && isString(e.value)) {
      setValue('queryType', e.value);
      setFormValue('queryType', e.value);
      if (form.queryTarget.length !== 0) {
        setFormValue('queryTarget', []);
        setTarget({ display: '' });
      }
    } else if (e.field === 'queryTarget') {
      if (isString(e.value)) {
        setFormValue('queryTarget', [e.value]);
        setValue('queryTarget', [e.value]);
      }
      if (Array.isArray(e.value)) {
        setFormValue('queryTarget', e.value);
        setValue('queryTarget', e.value);
      }
    }
  }

  useEffect(() => {
    register('queryLocation', { required: true });
    register('queryType', { required: true });
  }, [register]);

  return (
    <FormProvider {...formInstance}>
      <form
        onSubmit={handleSubmit(submitHandler)}
        className="w-full max-w-3xl mx-auto space-y-stack-md md:space-y-stack-lg text-left"
      >
        {/* Tool Header */}
        <div className="text-center space-y-base">
          <h2 className="font-headline-md text-headline-md text-slate-800 dark:text-on-surface">Diagnósticos de Rede</h2>
          <p className="text-slate-500 dark:text-on-surface-variant font-body-sm text-body-sm">
            Realize consultas BGP e de rede em tempo real em nossa infraestrutura.
          </p>
        </div>

        {/* Step 1: Location Selection */}
        <div className="space-y-stack-sm">
          <div className="flex items-center justify-between">
            <label className="font-label-caps text-label-caps text-slate-400 dark:text-on-surface-variant uppercase tracking-widest">
              01. Localidade
            </label>
            <span className="text-primary text-body-sm font-code-sm flex items-center gap-1">
              <span className="material-symbols-outlined text-[14px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                check_circle
              </span>
              Nós Ativos
            </span>
          </div>
          <QueryLocation onChange={handleChange} label={web.text.queryLocation} />
        </div>

        {/* Step 2: Query Type */}
        <AnimatePresence>
          {filtered.types.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 15 }}
              className="space-y-stack-sm"
            >
              <label className="font-label-caps text-label-caps text-slate-400 dark:text-on-surface-variant uppercase tracking-widest">
                02. Tipo de Consulta
              </label>
              <QueryType onChange={handleChange} label={web.text.queryType} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step 3: Target */}
        <AnimatePresence>
          {directive !== null && directive.fieldType !== null && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 15 }}
              className="space-y-stack-sm"
            >
              <label className="font-label-caps text-label-caps text-slate-400 dark:text-on-surface-variant uppercase tracking-widest">
                03. Destino
              </label>
              <QueryTarget
                name="queryTarget"
                register={register}
                onChange={handleChange}
                placeholder={directive.description}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action Area */}
        <AnimatePresence>
          {form.queryTarget.length !== 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="pt-stack-md md:pt-stack-lg pb-safe flex justify-center"
              style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))' }}
            >
              <SubmitButton />
            </motion.div>
          )}
        </AnimatePresence>
      </form>
    </FormProvider>
  );
};
